import { useEffect, useMemo } from 'react';
import { useNavigationComponentReceiver } from '../../hooks/internal';
import { DelayedIntervalScheduler, IdleTaskScheduler, ImmediateTaskScheduler } from '../../../common/scheduler';
import { useNativeCapabilities } from '../../hooks/internal/useNativeCapabilities';
import { useNavComponentsManager } from '../../hooks/useNavComponentsManager';
import { RenderNavComponent } from './RenderNavComponent';
import { ErrorBoundary } from '../../../common/components';
import { Logger } from 'simple-logging-lib';
import { PLATFORM_LOGGER } from '../../../config';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getComponentsScheduler = () => {
  if (IdleTaskScheduler.isSupported()) {
    // Schedule components to load when the browser is idle
    return new IdleTaskScheduler();
  }
  if ('jasmine' in window) {
    // Schedule components to load immediately in tests
    return new ImmediateTaskScheduler();
  }
  // Schedule components to load with a delay in older browsers
  return new DelayedIntervalScheduler();
};
const getInstantlyLoadedComponents = config => Object.entries(config).reduce((components, [name, {
  deferLoading = true
}]) => {
  if (!deferLoading) {
    components.add(name);
  }
  return components;
}, new Set());
export const RenderAllNavComponents = ({
  enableScheduler
}) => {
  useNativeCapabilities();
  useNavigationComponentReceiver();
  const {
    config,
    setComponentState
  } = useNavComponentsManager();
  const scheduler = useMemo(getComponentsScheduler, []);
  useEffect(() => {
    if (!enableScheduler) {
      return;
    }
    const components = getInstantlyLoadedComponents(config);

    /* Schedule loading of all components rather than diff because we don't know
     at what point previous run was cancelled. This is fine because we don't
     actually apply state changes if they result in no changes */
    components.forEach(name => {
      scheduler.enqueue(() => {
        setComponentState(name, state => Object.assign({}, state, {
          isLoaded: true
        }));
      });
    });
    scheduler.run();
    return () => {
      scheduler.cancel();
    };
  }, [config, scheduler, setComponentState, enableScheduler]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: Object.entries(config).map(([name, definition]) => /*#__PURE__*/_jsx(ErrorBoundary, {
      name: `RenderNavComponent-${name}`,
      logger: Logger.getLogger(PLATFORM_LOGGER),
      children: /*#__PURE__*/_jsx(RenderNavComponent, Object.assign({}, definition))
    }, name))
  });
};